<template>
  <div role="dialog" class="popup z-101 fadein absolute flex justify-center items-center">
    <div
      class="popup-wrapper max-w-lg h-auto flex flex-row-reverse rounded-xl bg-slate-middle"
      :style="{
        width: (state.liveTextLength ?? state.newNoteText.length) > 240 ? '80ch' : '40ch',
      }"
    >
      <div
        class="absolute h-8 w-8 mr-2 z-51 mt-3 p-1.5 bg-black/20 rounded-full flex justify-center items-center cursor-pointer"
        tabindex="0"
        ref="firstFocus"
        @pointerup.stop="close()"
        @keydown.enter="close()"
      >
        <img class="w-5 h-5 invert rotate-45" :src="Close" />
      </div>
      <div class="flex flex-col w-full h-full p-9">
        <p class="text-2xl font-bold text-slate-white mt-4 mb-7">{{ t('headline') }}</p>
        <div class="flex flex-col items-start w-full h-full font-middle">
          <div class="flex bg-white w-full items-center p-1.5 text-black z-51" :class="newNoteTextClass()">
            <textarea
              ref="newNoteText"
              id="new-note-input"
              v-model="state.newNoteText"
              class="w-full p-5 rounded-xl resize-none"
              :placeholder="t('placeholder')"
              @click.prevent="focusNewNoteText()"
              @touchmove.prevent="focusNewNoteText()"
              @input="autoResize"
            ></textarea>
          </div>

          <!-- This input handles the image upload -->
          <input id="file-chooser" class="hidden" type="file" accept="image/gif, image/jpeg, image/png" @change="checkFile($event)" />

          <div v-if="state.imageOption" class="bg-gray-white p-2 rounded-b-2xl w-full mb-3">
            <div class="flex flex-col" v-if="state.imageData">
              <div class="flex items-center justify-start">
                <div class="w-6 h-6 bg-blue-middle rounded-full mr-2">
                  <img class="p-1.5 invert" :src="Text" />
                </div>
                <input
                  :placeholder="t('title')"
                  v-model="state.attachment.title"
                  type="text"
                  class="flex flex-grow bg-gray-white p-1.5 rounded-2xl w-auto overflow-x-auto overflow-y-hidden text-black"
                />
              </div>
              <div class="flex items-center justify-start mt-1">
                <div class="w-6 h-6 bg-blue-middle rounded-full mr-2">
                  <img class="p-1.5 invert" :src="Quote" />
                </div>
                <input
                  :placeholder="t('description')"
                  v-model="state.attachment.description"
                  type="text"
                  class="flex flex-grow bg-gray-white p-1.5 rounded-2xl w-auto overflow-x-auto overflow-y-hidden text-black"
                />
              </div>
              <div class="my-2 max-h-64 overflow-scroll overflow-x-hidden rounded-2xl">
                <img class="rounded-2xl" :src="state.imageData.toString()" />
              </div>
            </div>
          </div>

          <div class="pt-5 flex justify-between items-center w-full">
            <!-- Cancel the note -->
            <button
              class="cursor-pointer p-2 px-3 text-md bg-black/20 rounded-full text-slate-white mr-2 capitalize"
              @click="close()"
              @touchstart.prevent="close()"
            >
              {{ t('cancel') }}
            </button>

            <!-- Image upload -->
            <button class="p-2 flex justify-center bg-black/20 items-center cursor-pointer rounded-full">
              <div v-if="!state.imageOption" @click.stop="invokeImageChooser()" @keydown.enter="invokeImageChooser()">
                <img class="w-5 h-5 invert" :src="Image" />
              </div>
              <div
                v-else
                class="rounded-full px-1 flex justify-center items-center relative text-red-400"
                @click.stop="removeImageUpload()"
                @keydown.enter="removeImageUpload()"
              >
                <span>{{ t('delete') }}&nbsp;{{ t('image') }}</span>
              </div>
            </button>

            <!-- Add the note to the Wall -->
            <button
              type="button"
              class="cursor-pointer p-2 px-3 bg-tw-blue-dark rounded-full text-md text-slate-white"
              @click.stop="console.log('ADD BUTTON CLICKED'); saveAndClose()"
              @keydown.enter="saveAndClose()"
            >
              {{ t('add') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { reactive, onMounted, ref, PropType, nextTick } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { AttachmentType, ParcelType, urlRegex } from '@/constants'
  import { Attachment, BoardEntry, DialogMessageType, INote, Note, Parcel } from '@/common/classes'
  import { ExtendedStoreResponse } from '@/common/interfaces'

  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'
  import { useNoteStore } from '@/composables/noteStore'

  import useCommonStore from '@/composables/common'
  import useDialogStore from '@/composables/dialog'
  import useParcelStore from '@/composables/parcel'
  import useFilterStore from '@/composables/filter'

  import Quote from '@/assets/icons/fontawesome/quote-left-solid.svg'
  import Image from '@/assets/icons/fontawesome/image-solid.svg'
  import Close from '@/assets/icons/fontawesome/plus-solid.svg'
  import Text from '@/assets/icons/fontawesome/text-solid.svg'
  import Cross from '@/assets/icons/close-black.svg'

  const messages = {
    no: {
      headline: 'Nytt bidrag',
      missingtitleordescription: 'Mangler tittel eller beskrivelse',
      title: 'Tittel',
    },
    en: {
      headline: 'New contribution',
      missingtitleordescription: 'Missing title or description',
      title: 'Title',
    },
  }

  const filter = useFilterStore()
  const common = useCommonStore()
  const userStore = useUserStore()
  const noteStore = useNoteStore()
  const wallStore = useWallStore()
  const parcelStore = useParcelStore()
  const dialogStore = useDialogStore()

  const emit = defineEmits<{
    (e: 'close-popup'): void
    (e: 'note-added'): void
  }>()

  const props = defineProps({
    placeNewNoteOnBoard: { type: Boolean, default: false },
    selectedBoardId: { type: String, required: true },
  })

  const state = reactive({
    newNoteText: '',
    attachment: {} as Attachment,
    imageOption: false,
    newNotePosition: [] as number[],
    imageData: '',
    liveTextLength: 0,
  })

  const newNoteText = ref<HTMLElement | null>(null)

  const { t } = useI18n({ messages })

  onMounted(() => {
    nextTick(() => focusNewNoteText())
    state.attachment.description = t('defaultdescription')

    const randomDeviation = Math.round(Math.random()) ? 1 : -1 * (Math.random() * 10 + 1)
    state.newNotePosition = [50 + randomDeviation, 50 - randomDeviation]
  })

  function autoResize(event: Event) {
    const el = event.target as HTMLTextAreaElement
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
    state.liveTextLength = el.value.length
    nextTick(() => {
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    })
  }

  async function parseTextForUrl(text: string) {
    const regex = urlRegex
    const url = regex.exec(text)
    if (url && url[0].length > 4) {
      const result = await common.actions.constructAttachmentFromScrape(url[0])
      if (result) return result
      else dialogStore.actions.pushMessage('', DialogMessageType.Error, t('scrapeUnsuccessful'))
    }
  }

  function checkFile(event: Event) {
    const input = event.target as HTMLInputElement // could also be solved via ref
    if (input.files && input.files[0]) {
      const newName = input.files[0].name
      state.attachment.title = newName.substring(0, newName.indexOf('.'))

      const reader = new FileReader()
      reader.onload = (e) => {
        state.attachment.type = AttachmentType.image
        if (e.target) state.imageData = e.target.result as string
        state.imageOption = true
        input.value = ''
      }
      if (input.files.length === 1 && input.files[0].size <= 5000000) reader.readAsDataURL(input.files[0])
      else {
        dialogStore.actions.pushMessage('', DialogMessageType.Error, t('filetoolarge'))
        removeImageUpload()
        state.imageOption = false
      }
    }
  }

  async function validateNewNote() {
    const creator = userStore.getters.getUserAsCreator.value
    if (creator && wallStore.getters.selectedWall.value) {
      let attachment = {
        type: AttachmentType.none,
        title: '',
        description: '',
        url: '',
      } as Attachment

      if (state.imageData && state.attachment.title) {
        attachment.title = state.attachment.title
        attachment.type = AttachmentType.image
        if (state.attachment.description === t('defaultdescription')) attachment.description = ''
        else attachment.description = state.attachment.description
      } else {
        const result = await parseTextForUrl(state.newNoteText)
        if (result) attachment = result
      }

      const noteStatus: Partial<Note['status']> = {
        createdBy: creator,
      }
      const noteSpec: INote = {
        wall_id: wallStore.getters.selectedWall.value._id,
        task_id: filter.getters.selectedTaskId.value,
        attachment,
        text: state.newNoteText,
        status: noteStatus,
      }
      const note = new Note(noteSpec)

      if (attachment.type === AttachmentType.image) {
        note.imageData = state.imageData
      }

      if (props.placeNewNoteOnBoard) {
        let boardEntry = {
          xpos: state.newNotePosition[1],
          ypos: state.newNotePosition[0],
          highlightColor: 'bg-blue-middle text-slate-white',
          wall_id: props.selectedBoardId,
          creator: creator,
        } as BoardEntry
        note.addBoardEntry(boardEntry.wall_id, boardEntry)
        const randomDeviation = Math.round(Math.random()) ? 1 : -1 * (Math.random() * 10 + 1)
        state.newNotePosition = [50 + randomDeviation, 50 - randomDeviation]
      }

      const res = await noteStore.actions.addNewNote(note)
      const wall = wallStore.getters.selectedWall.value
      if (res.success && res.body && wall) {
        const parcel = new Parcel({ parcelType: ParcelType.MessageCreate, body: res.body as Note }, userStore.getters.user.value, wall)
        await parcelStore.actions.sendParcel(wall._id, parcel)

        //Must emit that a note has been added to account for board toggle on/off
        emit('note-added')
      }
      state.newNoteText = ''
    }
  }

  function newNoteTextClass() {
    return state.imageOption ? 'rounded-t-xl' : 'rounded-xl'
  }

  function invokeImageUpload() {
    state.imageOption = true
  }

  function removeImageUpload() {
    state.imageOption = false
    state.attachment = {} as Attachment
    state.attachment.description = t('imagedescription')
  }

  function invokeImageChooser() {
    document.getElementById('file-chooser')?.click()
  }

  function close() {
    emit('close-popup')
  }

  async function saveAndClose() {
    if ((state.attachment.title && state.attachment.description) || state.attachment.type !== AttachmentType.image) {
      await validateNewNote()
      close()
    } else dialogStore.actions.pushMessage('', DialogMessageType.Error, t('missingtitleordescription'))
  }

  function focusNewNoteText() {
    setTimeout(() => {
      if (newNoteText.value) newNoteText.value.focus()
    }, 100)
  }
</script>

<style scoped>
  .task-input {
    min-height: 50px;
    width: 99%;
    margin-left: 2px;
  }
</style>
